import { TableCell, TableRow } from "@mui/material";
import { Loader2 } from "lucide-react";
import React, { useState } from "react";
import { FormattedNumber } from "react-intl";
import { Link } from "react-router-dom";
import { usePostV4ManufacturerByRouteManufacturerIdRequisitionAndRequisitionNumberEditItemRandmarSkuMutation } from "randmar-api-client";
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, useToast } from "randmarcomps";
import { useManufacturer } from "../../contexts/WithManufacturer";
import Path from "../../routes/Path";
import { RequisitionPartNumber } from "../../stores/document/Document";
import ProductDetailsModal from "../products/ProductDetailsModal";
import ButtonWithDialogConfirm from "../util/ButtonWithDialogConfirm";
import CurrencyUtils from "../util/CurrencyUtils";

interface IProps {
  index: number;
  partNumber: RequisitionPartNumber;
  requisitionNumber: string;
}

const RequisitionPartNumberRow: React.FC<IProps> = ({ partNumber, requisitionNumber }) => {
  const { applicationId } = useManufacturer();
  const [count, setCount] = useState(partNumber.quantityOrdered || 0);
  const [openUpdate, setOpenUpdate] = useState(false);
  const { toast } = useToast();

  const [editItem, { isLoading }] = usePostV4ManufacturerByRouteManufacturerIdRequisitionAndRequisitionNumberEditItemRandmarSkuMutation();

  const handleUpdateButton = () => {
    setOpenUpdate(true);
  };

  const handleCancel = () => {
    setOpenUpdate(false);
  };

  const handleOnCountChanged = (newValue: number) => {
    if (isNaN(newValue) || newValue < 0) newValue = 0;
    setCount(newValue);
  };

  const handleUpdateConfirm = async (): Promise<void> => {
    const quantityDifference = count - (partNumber.quantityOrdered || 0);
    if (quantityDifference === 0) {
      setOpenUpdate(false);
      return;
    }

    const successMessage =
      quantityDifference > 0
        ? `${quantityDifference} x ${partNumber.randmarSKU} added to requisition ${requisitionNumber}`
        : `${Math.abs(quantityDifference)} x ${partNumber.randmarSKU} removed from requisition ${requisitionNumber}`;

    const failMessage =
      quantityDifference > 0
        ? `${quantityDifference} x ${partNumber.randmarSKU} could not be added to requisition ${requisitionNumber}`
        : `${Math.abs(quantityDifference)} x ${partNumber.randmarSKU} could not be removed from requisition ${requisitionNumber}`;

    try {
      await editItem({
        routeManufacturerId: applicationId,
        requisitionNumber,
        randmarSku: partNumber.randmarSKU || "",
        quantity: count,
      }).unwrap();
      toast({ variant: "success", title: "Success", description: successMessage });
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: failMessage });
    } finally {
      setOpenUpdate(false);
    }
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    const successMessage = `${partNumber.randmarSKU} removed from requisition ${requisitionNumber}`;
    const failMessage = `${partNumber.randmarSKU} could not be removed from requisition ${requisitionNumber}`;
    try {
      await editItem({
        routeManufacturerId: applicationId,
        requisitionNumber,
        randmarSku: partNumber.randmarSKU || "",
        quantity: 0,
      }).unwrap();
      toast({ variant: "success", title: "Success", description: successMessage });
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: failMessage });
    }
  };

    if (!partNumber) return (<div>Loading part numbers</div>);

    return (
      <TableRow sx={{ "&:hover": { backgroundColor: "#f9f5ff" } }}>
        <TableCell>
          <div className="flex flex-col">
            <Link to={Path.product(applicationId, partNumber.randmarSKU)} className="font-medium hover:underline">
              {partNumber.mpn}
            </Link>
            <span className="text-sm text-gray-500">{partNumber.description}</span>
          </div>
        </TableCell>
        <TableCell align="center">
          <Input
            className="w-16 text-center"
            type="number"
            min={0}
            value={count}
            onChange={e => handleOnCountChanged(parseInt(e.target.value))}
          />
        </TableCell>
        <TableCell align="center" className="w-[150px] p-0">
          {count !== partNumber.quantityOrdered && (
            <Button
              id="ButtonConfirmItemChange"
              onClick={handleUpdateButton}
              disabled={openUpdate}>
              Update
            </Button>
          )}
          <Dialog open={openUpdate} onOpenChange={(isOpen) => !isOpen && !isLoading && handleCancel()}>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle className="text-center">Update {partNumber.mpn} quantity?</DialogTitle>
              </DialogHeader>
              {isLoading ? (
                <div className="flex min-h-[80px] items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : (
                <DialogFooter>
                  <Button variant="outline" onClick={handleCancel}>
                    Cancel
                  </Button>
                  <Button onClick={handleUpdateConfirm}>
                    Confirm
                  </Button>
                </DialogFooter>
              )}
            </DialogContent>
          </Dialog>
        </TableCell>
        <TableCell align="center">
          <FormattedNumber value={partNumber.unitPrice || 0} {...CurrencyUtils.format} />
        </TableCell>
        <TableCell align="center">
          <FormattedNumber value={(partNumber.unitPrice || 0) * (partNumber.quantityOrdered || 0)} {...CurrencyUtils.format} />
        </TableCell>
        <TableCell align="center">
          <ProductDetailsModal randmarSKU={partNumber.randmarSKU || ""} />
        </TableCell>
        <TableCell align="center" style={{ padding: "0 20px 0 0" }}>
          <ButtonWithDialogConfirm
            fab={true}
            onConfirmation={handleDeleteConfirm}
            buttonTitle={`Remove ${partNumber.mpn} from ${requisitionNumber}`}
          />
        </TableCell>
      </TableRow>
    );
};

export default RequisitionPartNumberRow;
